import React, { useEffect, useState } from "react";
import { useIsTablet } from "./useIsTablet";
import { useVirtualKeyboard } from "./useVirtualKeyboard";

const SIDEBAR_COLLAPSED_KEY = "dealos.sidebarCollapsed";
// Versioned key allows re-showing the welcome modal after major content updates
const WELCOME_MODAL_KEY = "dealos.welcomeModalDismissed.v1";

export interface MobileSidebar {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
}

export function useMobileSidebar(): MobileSidebar {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return {
    isOpen,
    toggle: () => setIsOpen((open) => !open),
    close: () => setIsOpen(false),
  };
}

export interface SidebarCollapse {
  isCollapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

/**
 * LHS sidebar collapse state is purely user-driven. Entering workspace
 * mode (RHS open) does NOT auto-collapse; closing the RHS does NOT
 * auto-restore. Whatever the user last set persists.
 */
export function useSidebarCollapse(): SidebarCollapse {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) ?? "false")
  );

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  return { isCollapsed, setCollapsed: setIsCollapsed };
}

export interface WelcomeModal {
  isOpen: boolean;
  open: () => void;
  dismiss: () => void;
}

/** Auto-opens on first visit only; re-openable from the header. */
export function useWelcomeModal(): WelcomeModal {
  const [isOpen, setIsOpen] = useState<boolean>(() => !localStorage.getItem(WELCOME_MODAL_KEY));

  return {
    isOpen,
    open: () => setIsOpen(true),
    dismiss: () => {
      setIsOpen(false);
      localStorage.setItem(WELCOME_MODAL_KEY, "true");
    },
  };
}

/**
 * On tablet, use visualViewport to handle the iOS virtual keyboard:
 * 1. Set height to visualViewport.height (the actual visible area)
 * 2. Counteract iOS visual viewport shift via translateY(offsetTop)
 */
export function useTabletKeyboardStyle(): React.CSSProperties | undefined {
  const isTablet = useIsTablet();
  const { isKeyboardOpen, offsetTop, viewportHeight } = useVirtualKeyboard({ enabled: isTablet });

  if (!isTablet || !(isKeyboardOpen || offsetTop > 0)) {
    return undefined;
  }

  return {
    height: `${viewportHeight}px`,
    transform: offsetTop > 0 ? `translateY(${offsetTop}px)` : undefined,
  };
}
